import {
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    viewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ColorPicker } from '@openng/optimus-ui/colorpicker';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { BaseInputComponentV2 } from '../base-input.component-v2';
import { ColorStylePipe } from '../../../../modules/shared/pipes/color-style.pipe';
import { ColorContrastPipe } from '../../../../modules/shared/pipes/color-contrast.pipe';
import { LoggedUserGlobalStore } from '../../../../store/logged-user.global-store';
import { ColorInputStore } from './color-input.store';
import { themeColors } from './color-input.types';
import { InputError } from '../input-error/input-error.component';
import { NgStyle } from '@angular/common';

@Component({
    selector: 'app-color-input',
    templateUrl: 'color-input.component.html',
    styleUrl: 'color-input.component.scss',
    providers: [ColorInputStore],
    imports: [
        ColorStylePipe,
        ColorPicker,
        FormsModule,
        ColorContrastPipe,
        Tooltip,
        TranslatePipe,
        InputError,
        NgStyle,
    ],
})
export class ColorInputComponent extends BaseInputComponentV2<string> {

    protected store = inject(ColorInputStore);
    protected loggedUserGlobalStore = inject(LoggedUserGlobalStore);

    public withCustomColors = input<boolean>(true);

    protected readonly themeColors = themeColors;
    protected colorPickers = viewChildren(ColorPicker);
    protected userColors = computed(() =>
        this.loggedUserGlobalStore.customColors(),
    );
    protected customColors = linkedSignal(() => {
        const colors = this.userColors().slice(0, 5);
        return [
            ...colors,
            ...Array<string | null>(5 - colors.length).fill(null),
        ];
    });
    protected inColorsList = computed(() => {
        const value = this.value();
        return (
            value == null ||
            themeColors.includes(value) ||
            this.customColors().includes(value)
        );
    });

    protected selectColor(
        event: MouseEvent,
        color: string | null,
        index: number | null = null,
    ): void {
        event.preventDefault();
        event.stopPropagation();

        if (color === null || this.disabled() || this.readonly()) return;

        this.value.set(color);
        this.touch.emit();

        if (index !== null)
            this.colorPickers().forEach((picker) => picker.hide());
    }

    protected showCustomColorPicker(event: MouseEvent, index: number): void {
        event.preventDefault();
        if (this.disabled() || this.readonly()) return;

        this.colorPickers().forEach((picker, pickerIndex) => {
            if (pickerIndex === index) picker.show();
            else picker.hide();
        });
    }

    protected updateCustomColor(index: number, color: string | null): void {
        this.customColors.update((colors) =>
            colors.map((item, itemIndex) =>
                itemIndex === index ? color : item,
            ),
        );
    }

    protected async persistCustomColors(): Promise<void> {
        const colors = this.customColors().filter((color): color is string =>
            Boolean(color),
        );

        await this.store.api.patchUserSettings({
            userId: this.loggedUserGlobalStore.currentUserId() ?? '',
            colors: JSON.stringify(colors),
        });
        this.loggedUserGlobalStore.customColors.set(colors);
    }
}
